import { Ban, DollarSign, Edit, Eye, PackageCheck, Trash2 } from 'lucide-react';
import type { RowActionItem, RowMenuEntry } from '../components/transactions/RowActionsMenu';
import type { PurchaseTransaction, PurchaseTransactionAction } from '../types/purchaseTransaction';
import { canEditPurchaseTransaction } from './billEditability';
import {
  canDeleteOrVoidPurchases,
  canManageBills,
} from './purchaseTransactionPermissions';

export interface PurchaseBillRowActionHandlers {
  onView: () => void;
  onEdit: () => void;
  onReceive: () => void;
  onDelete: () => void;
  onVoid: () => void;
  onPayBill: () => void;
}

export interface PurchaseBillRowActionsLayout {
  inlineActions: RowActionItem[];
  menuEntries: RowMenuEntry[];
}

const hasAction = (txn: PurchaseTransaction, action: PurchaseTransactionAction) =>
  txn.available_actions.includes(action);

const buildActionCatalog = (
  txn: PurchaseTransaction,
  handlers: PurchaseBillRowActionHandlers
): Map<string, RowActionItem> => {
  const catalog = new Map<string, RowActionItem>();
  const canManage = canManageBills();
  const canVoidOrDelete = canDeleteOrVoidPurchases();
  const canEdit = canEditPurchaseTransaction(txn) && canManage;

  if (hasAction(txn, 'view')) {
    catalog.set('view', {
      id: 'view',
      label: 'View',
      icon: Eye,
      onClick: handlers.onView,
    });
  }

  if (canEdit) {
    catalog.set('edit', {
      id: 'edit',
      label: 'Edit',
      icon: Edit,
      onClick: handlers.onEdit,
    });
  }

  if (hasAction(txn, 'receive') && canManage) {
    catalog.set('receive', {
      id: 'receive',
      label: 'Receive',
      icon: PackageCheck,
      variant: 'primary',
      showLabel: true,
      onClick: handlers.onReceive,
    });
  }

  if (hasAction(txn, 'pay_bill')) {
    catalog.set('pay_bill', {
      id: 'pay_bill',
      label: 'Pay',
      icon: DollarSign,
      variant: 'primary',
      showLabel: true,
      onClick: handlers.onPayBill,
    });
  }

  if (hasAction(txn, 'delete') && canVoidOrDelete) {
    catalog.set('delete', {
      id: 'delete',
      label: 'Delete draft',
      icon: Trash2,
      variant: 'destructive',
      onClick: handlers.onDelete,
    });
  }

  if (hasAction(txn, 'void') && canVoidOrDelete) {
    catalog.set('void', {
      id: 'void',
      label: 'Void',
      icon: Ban,
      variant: 'destructive',
      onClick: handlers.onVoid,
    });
  }

  return catalog;
};

const getInlineActionIds = (catalog: Map<string, RowActionItem>): string[] => {
  const inline: string[] = [];

  if (catalog.has('receive')) inline.push('receive');
  if (catalog.has('pay_bill')) inline.push('pay_bill');
  if (catalog.has('edit') && inline.length < 2) inline.push('edit');
  if (inline.length === 0 && catalog.has('view')) inline.push('view');

  return inline;
};

const MENU_GROUPS: string[][] = [
  ['view', 'edit', 'receive', 'pay_bill'],
  ['delete', 'void'],
];

/** Primary actions inline; remaining actions grouped in overflow menu. */
export const buildPurchaseBillRowActions = (
  txn: PurchaseTransaction,
  handlers: PurchaseBillRowActionHandlers
): PurchaseBillRowActionsLayout => {
  const catalog = buildActionCatalog(txn, handlers);
  const inlineIds = new Set(getInlineActionIds(catalog));

  const inlineActions = getInlineActionIds(catalog)
    .map((id) => catalog.get(id))
    .filter((item): item is RowActionItem => item != null);

  const menuEntries: RowMenuEntry[] = [];

  for (const group of MENU_GROUPS) {
    const groupItems = group
      .filter((id) => !inlineIds.has(id) && catalog.has(id))
      .map((id) => catalog.get(id)!);

    if (groupItems.length === 0) continue;
    if (menuEntries.length > 0) menuEntries.push({ type: 'separator' });
    menuEntries.push(...groupItems);
  }

  return { inlineActions, menuEntries };
};
